import { useCallback, useState } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { LocationData, UniqueBirdDetection } from '../types';
import { supabase } from '../utils/supabase';


export function useDetectionPersistence() {
  const { user } = useAuth();
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  const createDetectionSession = useCallback(async (location: LocationData) => {
    if (!user) {return null;}

    try {
      const { data, error } = await supabase
        .from('detection_sessions')
        .insert([
          {
            user_id: user.id,
            location_lat: location.lat,
            location_lng: location.lng,
            location_name: location.name,
            started_at: new Date().toISOString(),
            total_detections: 0,
            unique_species: 0,
          },
        ])
        .select()
        .single();

      if (error) {throw error;}
      if (data) {
        setCurrentSessionId(data.id);
      }
      return data;
    } catch (error) {
      console.error('Error creating detection session:', error);
      return null;
    }
  }, [user]);

  const saveDetectionResult = useCallback(async (result: any, location?: LocationData) => {
    if (!user || !result.detections || !currentSessionId) {return;}

    try {
      // Save each detection
      const validDetections = result.detections.filter((detection: any) => 
        detection.species && 
        detection.common_name && 
        typeof detection.confidence === 'number'
      );

      if (validDetections.length === 0) {return;}

      const { error } = await supabase
        .from('bird_detections')
        .insert(
          validDetections.map((detection: any) => ({
            session_id: currentSessionId,
            user_id: user.id,
            species: detection.species,
            common_name: detection.common_name,
            confidence: detection.confidence,
            start_time: detection.start_time || 0,
            end_time: detection.end_time || 0,
            audio_length: (detection.end_time || 0) - (detection.start_time || 0),
            detected_at: new Date().toISOString(),
            location_lat: location?.lat,
            location_lng: location?.lng,
            location_name: location?.name,
          }))
        );

      if (error) {throw error;}
    } catch (error) {
      console.error('Error saving bird detections:', error);
    }
  }, [user, currentSessionId]);

  const updateSessionStats = useCallback(async (totalDetections: number, uniqueSpecies: number) => {
    if (!user) {return;}

    try {
      // Get the latest session for this user
      const { data: sessions, error: sessionError } = await supabase
        .from('detection_sessions')
        .select('id')
        .eq('user_id', user.id)
        .order('started_at', { ascending: false })
        .limit(1);

      if (sessionError || !sessions || sessions.length === 0) {return;}

      const { error } = await supabase
        .from('detection_sessions')
        .update({
          total_detections: totalDetections,
          unique_species: uniqueSpecies,
          ended_at: new Date().toISOString(),
        })
        .eq('id', sessions[0].id);

      if (error) {throw error;}
    } catch (error) {
      console.error('Error updating session stats:', error);
    }
  }, [user]);

  const updateUserLibrary = useCallback(async (uniqueDetections: Map<string, UniqueBirdDetection>) => {
    if (!user || uniqueDetections.size === 0) {return;}

    try {
      const libraryEntries = Array.from(uniqueDetections.values()).map(detection => ({
        user_id: user.id,
        species: detection.species,
        common_name: detection.common_name,
        first_detected: detection.first_detected,
        last_detected: detection.last_detected,
        max_confidence: detection.max_confidence,
        min_confidence: detection.max_confidence, // For now, use max_confidence as we don't track min
        avg_confidence: detection.max_confidence, // For now, use max_confidence as we don't track avg
        detection_count: detection.session_detections,
      }));

      const { error } = await supabase
        .from('user_bird_library')
        .upsert(libraryEntries, {
          onConflict: 'user_id,species',
          ignoreDuplicates: false,
        });

      if (error) {throw error;}
    } catch (error) {
      console.error('Error updating user library:', error);
    }
  }, [user]);

  const endDetectionSession = useCallback(async () => {
    // Clear the current session ID
    setCurrentSessionId(null);
    return Promise.resolve();
  }, []);

  const getUserLibrary = useCallback(async () => {
    if (!user) {return [];}

    try {
      // Get library entries
      const { data: libraryData, error: libraryError } = await supabase
        .from('user_bird_library')
        .select('*')
        .eq('user_id', user.id)
        .order('last_detected', { ascending: false });

      if (libraryError) {throw libraryError;}

      // Get actual detection counts from bird_detections table
      const { data: detectionCounts, error: countError } = await supabase
        .from('bird_detections')
        .select('species, common_name')
        .eq('user_id', user.id);

      if (countError) {throw countError;}

      // Count detections per species
      const countsMap = new Map<string, number>();
      detectionCounts?.forEach(detection => {
        const count = countsMap.get(detection.species) || 0;
        countsMap.set(detection.species, count + 1);
      });

      // Update library data with actual counts
      const updatedLibrary = libraryData?.map(entry => ({
        ...entry,
        detection_count: countsMap.get(entry.species) || 0
      })) || [];

      return updatedLibrary;
    } catch (error) {
      console.error('Error fetching user library:', error);
      return [];
    }
  }, [user]);

  const getUserSessions = useCallback(async () => {
    if (!user) {return [];}

    try {
      // Get sessions
      const { data: sessionsData, error: sessionsError } = await supabase
        .from('detection_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('started_at', { ascending: false });

      if (sessionsError) {throw sessionsError;}

      // Get detection counts per session
      const { data: sessionStats, error: statsError } = await supabase
        .from('bird_detections')
        .select('session_id, species')
        .eq('user_id', user.id);

      if (statsError) {throw statsError;}

      // Calculate stats per session
      const sessionStatsMap = new Map<string, { total: number; uniqueSpecies: Set<string> }>();
      sessionStats?.forEach(detection => {
        if (!detection.session_id) {return;}
        
        const stats = sessionStatsMap.get(detection.session_id) || { 
          total: 0, 
          uniqueSpecies: new Set<string>() 
        };
        stats.total++;
        stats.uniqueSpecies.add(detection.species);
        sessionStatsMap.set(detection.session_id, stats);
      });

      // Update sessions with actual counts
      const updatedSessions = sessionsData?.map(session => {
        const stats = sessionStatsMap.get(session.id);
        return {
          ...session,
          total_detections: stats?.total || 0,
          unique_species: stats?.uniqueSpecies.size || 0
        };
      }) || [];

      return updatedSessions;
    } catch (error) {
      console.error('Error fetching user sessions:', error);
      return [];
    }
  }, [user]);

  const getUserDetections = useCallback(async () => {
    if (!user) {return [];}

    try {
      const { data, error } = await supabase
        .from('bird_detections')
        .select('*')
        .eq('user_id', user.id)
        .order('detected_at', { ascending: false });

      if (error) {throw error;}
      return data || [];
    } catch (error) {
      console.error('Error fetching user detections:', error);
      return [];
    }
  }, [user]);

  return {
    createDetectionSession,
    saveDetectionResult,
    updateSessionStats,
    updateUserLibrary,
    endDetectionSession,
    getUserLibrary,
    getUserSessions,
    getUserDetections,
    isAuthenticated: !!user,
  };
}